import * as React from "react";
import { Avatar } from "baseui/avatar";
import { Block } from "baseui/block";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { LabelSmall } from "kanopi-components/typography";
import { NavItemProps } from "kanopi-components/LeftNav/LeftNavItem";
import { SidePadder } from "kanopi-components/SpaceWrapper/SidePadder";
import { StatefulPopover } from "baseui/popover";
import { StyledLink } from "baseui/link";
import { StyledNavItem } from "baseui/side-navigation";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { Icon } from "kanopi-components/icons/Icon";
import { Link } from "@reach/router";
import { useGate } from "statsig-react";
import { useUser } from "kw/auth/useUser";

type THeaderProps = {
  userName: string;
  mainItems: NavItemProps[];
  userItems: NavItemProps[];
  height?: string;
};
export const Header: React.FC<THeaderProps> = ({
  mainItems,
  height = "82px",
  userItems,
  userName,
}) => {
  const { value: orgSsoEnabled } = useGate("k-166-admin-sso-config");
  const user = useUser();

  const shouldDisplayGlobalSettingsLink = React.useMemo(
    () => orgSsoEnabled && user?.allowAdminPortalAccess,
    [orgSsoEnabled, user?.allowAdminPortalAccess]
  );

  const [, theme] = useStyletron();

  return (
    <Block
      display="flex"
      alignContent="center"
      height={height}
      justifyContent="right"
      data-testid="portalHeader"
    >
      <HorizontalSpacer justifyContent="right" gridColumnGap="24px" alignItems="center">
        <HeaderNavItems items={mainItems} data-testId="portalHeaderMainItems" />
        {shouldDisplayGlobalSettingsLink ? (
          <Link
            to="/admin/global-settings"
            aria-label="global settings"
            style={{
              color: theme.colors.contentPrimary,
            }}
          >
            <Icon icon="settings" />
          </Link>
        ) : null}

        <UserMenu userName={userName} items={userItems} />
      </HorizontalSpacer>
    </Block>
  );
};

const HeaderNavItems: React.FC<{ items: NavItemProps[] }> = ({ items }) => {
  return (
    <HorizontalSpacer>
      {items.map((item, index) => {
        return (
          <StyledNavItem key={index} {...item} data-testid={item?.item?.itemId}>
            {item?.icon}
          </StyledNavItem>
        );
      })}
    </HorizontalSpacer>
  );
};

const UserMenu: React.FC<{ userName: string; items: NavItemProps[] }> = ({
  userName,
  items,
}) => {
  const [, theme] = useStyletron();
  return (
    <StatefulPopover
      content={() => (
        <SidePadder
          paddingTop={theme.sizing.scale700}
          paddingBottom={theme.sizing.scale700}
        >
          <VerticalSpacer gap="inner-section" data-testid="portalHeaderUserMenuItems">
            <LabelSmall>{userName}</LabelSmall>
            {items.map((item, index) => {
              return (
                <StyledLink
                  href="javascript:void(0);"
                  key={index}
                  onClick={item?.onSelect}
                >
                  <HorizontalSpacer>
                    {item?.icon}
                    {item?.title}
                  </HorizontalSpacer>
                </StyledLink>
              );
            })}
          </VerticalSpacer>
        </SidePadder>
      )}
      placement="bottomRight"
      overrides={{
        Body: {
          style: {
            zIndex: theme.zIndex.fixedComponent,
          },
        },
      }}
      returnFocus
      autoFocus
    >
      <StyledLink href="javascript:void(0);" data-testid="portalHeaderUserMenu">
        <Avatar
          name={userName}
          size={theme.sizing.scale950}
          overrides={{
            Root: {
              style: ({ $theme }) => ({
                backgroundColor: $theme.colors.accent,
              }),
            },
          }}
        />
      </StyledLink>
    </StatefulPopover>
  );
};
